import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaArrowDown, FaArrowUp, FaBuilding, FaClock, FaExclamationTriangle, FaHashtag, FaMinus, FaPaperclip, FaSyncAlt, FaTicketAlt, FaUser } from 'react-icons/fa';
import { Button } from '../../components/Button';
import { PageLayout } from '../../components/layout';
import { useIsKioskOwner } from '../../hooks/useCurrentRole';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { getKiosks } from '../../services/KioskService';
import {
  cancelSupportRequest,
  createSupportRequest,
  getAllSupportRequests,
  getMySupportRequests,
  ownerUpdateSupportRequest,
  updateSupportRequestStatus,
  uploadSupportRequestImage,
} from '../../services/SupportRequestService';
import { Kiosk } from '../../types/kiosk.type';
import {
  SupportRequest,
  SupportRequestAttachment,
  SupportRequestPriority,
  SupportRequestStatus,
} from '../../types/supportRequest.types';
import './SupportRequestsPage.css';

const STATUS_OPTIONS: SupportRequestStatus[] = ['OPEN', 'IN_PROGRESS', 'WAITING_ON_OWNER', 'RESOLVED', 'CLOSED'];
const PRIORITY_OPTIONS: SupportRequestPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const STATUS_LABELS: Record<SupportRequestStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  WAITING_ON_OWNER: 'Waiting on Owner',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

const PRIORITY_META: Record<
  SupportRequestPriority,
  { label: string; icon: typeof FaArrowDown; className: string }
> = {
  LOW: { label: 'Low', icon: FaArrowDown, className: 'sr-priority--low' },
  MEDIUM: { label: 'Medium', icon: FaMinus, className: 'sr-priority--medium' },
  HIGH: { label: 'High', icon: FaArrowUp, className: 'sr-priority--high' },
  URGENT: { label: 'Urgent', icon: FaExclamationTriangle, className: 'sr-priority--urgent' },
};

const VIDEO_FILE_PATTERN = /\.(mp4|mov|m4v|webm|ogg|ogv|avi|mkv)(\?.*)?$/i;

const isVideoAttachment = (url?: string | null, fileName?: string | null) => {
  const candidate = `${fileName || ''} ${url || ''}`;
  return VIDEO_FILE_PATTERN.test(candidate);
};

const renderAttachmentMedia = (url: string, alt: string, fileName?: string | null) => {
  if (isVideoAttachment(url, fileName)) {
    return <video src={url} controls preload="metadata" />;
  }

  return <img src={url} alt={alt} />;
};

function PriorityBadge({ value }: { value: SupportRequestPriority }) {
  const meta = PRIORITY_META[value];
  const Icon = meta.icon;

  return (
    <span className={`sr-priority-badge ${meta.className}`}>
      <Icon />
      {meta.label}
    </span>
  );
}

const groupAttachmentsByHistory = (request: SupportRequest): Record<number, SupportRequestAttachment[]> => {
  const grouped: Record<number, SupportRequestAttachment[]> = {};
  const fallbackEventId = request.statusHistory[0]?.id;

  if (!request.attachments.length) {
    return grouped;
  }

  request.attachments.forEach((attachment) => {
    const eventId = attachment.supportRequestStatusEventId ?? fallbackEventId;

    if (!eventId) {
      return;
    }

    grouped[eventId] = [...(grouped[eventId] || []), attachment];
  });

  return grouped;
};

function OwnerSupportView() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [ownerUpdatingId, setOwnerUpdatingId] = useState<number | null>(null);
  const [ownerUpdateNote, setOwnerUpdateNote] = useState('');
  const [ownerNoteAttachmentUrls, setOwnerNoteAttachmentUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [kioskId, setKioskId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<SupportRequestPriority>('MEDIUM');
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [isComposeOpen, setIsComposeOpen] = useState(false);

  const loadKiosks = useCallback(async () => {
    try {
      const list = await getKiosks();
      setKiosks(list);
      setKioskId((current) => current || list[0]?.kioskId || '');
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to load kiosks.');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getMySupportRequests();
      setRequests(rows);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to load support requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadKiosks();
  }, [load, loadKiosks]);

  useEffect(() => {
    if (requests.length === 0) {
      setSelectedRequestId(null);
      return;
    }

    if (selectedRequestId && !requests.some((request) => request.id === selectedRequestId)) {
      setSelectedRequestId(null);
    }
  }, [requests, selectedRequestId]);

  const selectedRequest = useMemo(
    () => requests.find((request) => request.id === selectedRequestId) || null,
    [requests, selectedRequestId],
  );

  const selectedHistoryAttachments = useMemo(
    () => (selectedRequest ? groupAttachmentsByHistory(selectedRequest) : {}),
    [selectedRequest],
  );

  useEffect(() => {
    if (!selectedRequest) {
      setOwnerUpdateNote('');
      setOwnerNoteAttachmentUrls([]);
      return;
    }

    if (selectedRequest.status !== 'WAITING_ON_OWNER') {
      setOwnerUpdateNote('');
      setOwnerNoteAttachmentUrls([]);
      return;
    }

    setOwnerUpdateNote('');
    setOwnerNoteAttachmentUrls([]);
  }, [selectedRequest]);

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    if (attachmentUrls.length + files.length > 5) {
      showErrorToast('You can attach up to 5 files (images/videos) per support request.');
      return;
    }

    setUploading(true);
    try {
      const nextUrls: string[] = [];
      for (const file of files) {
        const uploadedUrl = await uploadSupportRequestImage(file);
        nextUrls.push(uploadedUrl);
      }

      setAttachmentUrls((current) => [...current, ...nextUrls]);
      showSuccessToast('Attachment uploaded.');
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to upload one or more files.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const submitRequest = async () => {
    if (!kioskId.trim()) {
      showErrorToast('Please select a kiosk for this support request.');
      return;
    }

    if (!subject.trim()) {
      showErrorToast('Please enter a subject.');
      return;
    }

    if (!description.trim()) {
      showErrorToast('Please describe your issue.');
      return;
    }

    setSubmitting(true);
    try {
      const created = await createSupportRequest({
        kioskId: kioskId.trim(),
        subject: subject.trim(),
        description: description.trim(),
        priority,
        attachmentUrls,
      });

      setRequests((current) => [created, ...current]);
      setSubject('');
      setDescription('');
      setPriority('MEDIUM');
      setAttachmentUrls([]);
      setSelectedRequestId(null);
      setIsComposeOpen(false);
      showSuccessToast(`Support request ${created.ticketNumber || `#${created.id}`} submitted.`);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to submit support request.');
    } finally {
      setSubmitting(false);
    }
  };

  const canOwnerCancel = (status: SupportRequestStatus) => {
    return status === 'OPEN' || status === 'IN_PROGRESS' || status === 'WAITING_ON_OWNER';
  };

  const handleCancelRequest = async (request: SupportRequest) => {
    setCancellingId(request.id);
    try {
      const result = await cancelSupportRequest(request.id, {
        reason: 'Cancelled by owner: support not required or report was invalid.',
      });
      setRequests((current) => current.map((item) => (item.id === request.id ? result.request : item)));
      showSuccessToast(`Support request ${result.request.ticketNumber || `#${result.request.id}`} cancelled.`);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to cancel support request.');
    } finally {
      setCancellingId(null);
    }
  };

  const handleOwnerUpdate = async (request: SupportRequest) => {
    const note = ownerUpdateNote.trim();
    if (!note) {
      showErrorToast('Please add an update note before sending this ticket back to admin.');
      return;
    }

    setOwnerUpdatingId(request.id);
    try {
      const result = await ownerUpdateSupportRequest(request.id, {
        note,
        attachmentUrls: ownerNoteAttachmentUrls,
      });
      setRequests((current) => current.map((item) => (item.id === request.id ? result.request : item)));
      setSelectedRequestId(null);
      setOwnerUpdateNote('');
      setOwnerNoteAttachmentUrls([]);
      showSuccessToast(`Support request ${result.request.ticketNumber || `#${result.request.id}`} moved back to Open for admin review.`);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to send update to admin.');
    } finally {
      setOwnerUpdatingId(null);
    }
  };

  const handleOwnerNoteUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    if (ownerNoteAttachmentUrls.length + files.length > 5) {
      showErrorToast('You can attach up to 5 files (images/videos) to a ticket note.');
      return;
    }

    setUploading(true);
    try {
      const nextUrls: string[] = [];
      for (const file of files) {
        const uploadedUrl = await uploadSupportRequestImage(file);
        nextUrls.push(uploadedUrl);
      }

      setOwnerNoteAttachmentUrls((current) => [...current, ...nextUrls]);
      showSuccessToast('Note attachment uploaded.');
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to upload note attachment.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <PageLayout title="Support Requests">
        <div className="sr-loading">Loading support requests...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Support Requests" subtitle="Track support like an inbox, with small rows and ticket popups.">
      <div className="sr-inbox sr-inbox--owner">
        <section className="sr-card">
          <div className="sr-card-header">
            <div>
              <h2>My support requests</h2>
            </div>
            <div className="sr-ticket-actions">
              <Button variant="secondary" size="sm" startIcon={<FaTicketAlt />} onClick={() => setIsComposeOpen(true)}>
                New ticket
              </Button>
              <Button variant="tertiary" size="sm" startIcon={<FaSyncAlt />} onClick={() => { void load(); }}>
                Refresh
              </Button>
            </div>
          </div>

          {requests.length === 0 ? (
            <div className="sr-empty">No support requests yet. Create your first ticket.</div>
          ) : (
            <div className="sr-ticket-list sr-ticket-list--dense">
              {requests.map((request) => (
                <button
                  key={request.id}
                  type="button"
                  className={`sr-ticket-row sr-ticket-row--compact ${selectedRequestId === request.id ? 'sr-ticket-row--active' : ''}`}
                  aria-pressed={selectedRequestId === request.id}
                  onClick={() => setSelectedRequestId(request.id)}
                >
                  <div className="sr-ticket-row__line">
                    <div className="sr-ticket-row__subject-line">
                      <span className="sr-ticket"><FaHashtag /> {request.ticketNumber || request.id}</span>
                      <h3>{request.subject}</h3>
                    </div>
                    <div className="sr-ticket-row__summary sr-ticket-row__summary--owner">
                      <span className="sr-meta-icon"><FaBuilding /> {request.kioskId}</span>
                      <span className="sr-meta-icon"><FaClock /> {formatDate(request.updatedAt)}</span>
                      <span className="sr-meta-icon"><FaPaperclip /> {request.attachments.length}</span>
                      <span className="sr-priority-inline"><PriorityBadge value={request.priority} /></span>
                    </div>
                    <div className={`sr-status sr-status--${request.status.toLowerCase()}`}>
                      {STATUS_LABELS[request.status]}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {isComposeOpen && createPortal(
        <div className="sr-popup-overlay" onClick={(event) => { if (event.target === event.currentTarget) setIsComposeOpen(false); }}>
          <div className="sr-popup-card sr-popup-card--compose">
            <div className="sr-modal-header sr-modal-header--compose">
              <div className="sr-modal-heading">
                <span className="sr-badge sr-badge--primary">New ticket</span>
                <h3>Create support request</h3>
              </div>
              <Button variant="tertiary" size="sm" onClick={() => setIsComposeOpen(false)}>Close</Button>
            </div>

            <div className="sr-popup-card__body">
              <div className="sr-compose-shell">
              <div className="sr-compose-grid sr-compose-grid--split">
                <label className="sr-field">
                  <span>Kiosk</span>
                  <select className="sr-select" value={kioskId} onChange={(event) => setKioskId(event.target.value)} disabled={kiosks.length === 0}>
                    {kiosks.length === 0 ? <option value="">Loading kiosks...</option> : kiosks.map((kiosk) => <option key={kiosk.kioskId} value={kiosk.kioskId}>{kiosk.name || kiosk.kioskId} ({kiosk.kioskId})</option>)}
                  </select>
                </label>

                <label className="sr-field sr-field--small sr-field--priority">
                  <span>Priority</span>
                  <div className="sr-priority-field">
                    <div className="sr-priority-select-wrap">
                      <span className={`sr-priority-select-pill ${PRIORITY_META[priority].className}`}>
                        {(() => {
                          const Icon = PRIORITY_META[priority].icon;
                          return <Icon />;
                        })()}
                        {PRIORITY_META[priority].label}
                      </span>
                      <select className="sr-select sr-select--priority" value={priority} onChange={(event) => setPriority(event.target.value as SupportRequestPriority)}>
                        {PRIORITY_OPTIONS.map((option) => <option key={option} value={option}>{option.replace('_', ' ')}</option>)}
                      </select>
                    </div>
                  </div>
                </label>
              </div>

              <label className="sr-field">
                <span>Subject</span>
                <input className="sr-input" value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Example: KIOSK currency not updating" />
              </label>

              <label className="sr-field">
                <span>Description</span>
                <textarea className="sr-textarea" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What happened, what you expected, and any steps to reproduce." rows={6} />
              </label>

              <div className="sr-attachments-row">
                <label className="sr-upload-btn">
                  <FaPaperclip /> Attach files
                  <input type="file" accept="image/*,video/*" multiple onChange={handleUpload} disabled={uploading || submitting} />
                </label>
                <span className="sr-upload-meta">Up to 5 files (images/videos).</span>
              </div>

              {attachmentUrls.length > 0 && (
                <div className="sr-attachment-preview-grid">
                  {attachmentUrls.map((url, index) => (
                    <div key={url} className="sr-attachment-preview-item">
                      {renderAttachmentMedia(url, `Attachment ${index + 1}`)}
                      <button type="button" className="sr-remove-link" onClick={() => setAttachmentUrls((current) => current.filter((item) => item !== url))}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

                <div className="sr-actions sr-actions--footer">
                  <Button
                    variant="primary"
                    onClick={() => { void submitRequest(); }}
                    loading={submitting}
                    disabled={uploading}
                    startIcon={<FaTicketAlt />}
                  >
                    Submit request
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {selectedRequest && createPortal(
        <div className="sr-popup-overlay" role="dialog" aria-modal="true" aria-labelledby="sr-ticket-dialog-title" onClick={(event) => { if (event.target === event.currentTarget) setSelectedRequestId(null); }}>
          <div className="sr-popup-card sr-popup-card--wide">
            <div className="sr-modal-header">
              <div className="sr-modal-heading sr-modal-heading--inline">
                <span className="sr-ticket sr-ticket--big">{selectedRequest.ticketNumber || `#${selectedRequest.id}`}</span>
                <h3 id="sr-ticket-dialog-title">{selectedRequest.subject}</h3>
              </div>
              <Button variant="tertiary" size="sm" onClick={() => setSelectedRequestId(null)}>Close</Button>
            </div>
            <div className="sr-popup-card__body">
              <div className="sr-modal-meta">
                <span className={`sr-status sr-status--${selectedRequest.status.toLowerCase()}`}>{STATUS_LABELS[selectedRequest.status]}</span>
                <span className="sr-priority-inline"><PriorityBadge value={selectedRequest.priority} /></span>
                <span className="sr-meta-icon"><FaBuilding /> {selectedRequest.kioskId}</span>
                <span className="sr-meta-icon"><FaClock /> {formatDate(selectedRequest.updatedAt)}</span>
                <span className="sr-meta-icon"><FaPaperclip /> {selectedRequest.attachments.length}</span>
              </div>
              {selectedRequest.status === 'WAITING_ON_OWNER' && (
                <div className="sr-owner-waiting-hint">
                  Admin requested more details. Add your note below and send update to reopen this ticket.
                </div>
              )}
              <div className="sr-detail-surface">
                {selectedRequest.status === 'WAITING_ON_OWNER' && (
                  <div className="sr-update-panel sr-update-panel--owner">
                    <div className="sr-update-panel__header">
                      <h4>Update ticket</h4>
                    </div>
                    <div className="sr-update-form">
                      <label className="sr-field sr-field--grow">
                        <span>Update note (required)</span>
                        <textarea
                          className="sr-textarea sr-textarea--compact"
                          rows={3}
                          value={ownerUpdateNote}
                          onChange={(event) => setOwnerUpdateNote(event.target.value)}
                          placeholder="Share requested details so admin can continue support"
                        />
                      </label>
                      <div className="sr-attachments-row">
                        <label className="sr-upload-btn">
                          <FaPaperclip /> Add note files
                          <input type="file" accept="image/*,video/*" multiple onChange={handleOwnerNoteUpload} disabled={uploading || ownerUpdatingId === selectedRequest.id} />
                        </label>
                        <span className="sr-upload-meta">Attach screenshots/videos or evidence to this response.</span>
                      </div>

                      {ownerNoteAttachmentUrls.length > 0 && (
                        <div className="sr-attachment-preview-grid">
                          {ownerNoteAttachmentUrls.map((url, index) => (
                            <div key={url} className="sr-attachment-preview-item">
                              {renderAttachmentMedia(url, `Owner note attachment ${index + 1}`)}
                              <button type="button" className="sr-remove-link" onClick={() => setOwnerNoteAttachmentUrls((current) => current.filter((item) => item !== url))}>Remove</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="sr-update-form__actions">
                        <Button
                          variant="primary"
                          loading={ownerUpdatingId === selectedRequest.id}
                          onClick={() => {
                            void handleOwnerUpdate(selectedRequest);
                          }}
                        >
                          Send update to admin
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {selectedRequest.resolutionNote && <div className="sr-resolution-box"><strong>Latest update</strong><p>{selectedRequest.resolutionNote}</p></div>}
                {selectedRequest.statusHistory.length > 0 && <div className="sr-history-block"><h4>Ticket history</h4><div className="sr-history-list">{selectedRequest.statusHistory.map((entry) => <article key={entry.id} className="sr-history-item"> <p><strong>{entry.fromStatus || 'NEW'}</strong>{' -> '}<strong>{entry.toStatus}</strong></p> <p>{entry.note || 'No note provided'}</p> {(selectedHistoryAttachments[entry.id] || []).length > 0 && <div className="sr-history-attachments-grid">{(selectedHistoryAttachments[entry.id] || []).map((attachment) => <a key={attachment.id} href={attachment.imageUrl} target="_blank" rel="noreferrer" className="sr-attachment-tile">{renderAttachmentMedia(attachment.imageUrl, attachment.fileName || 'Attachment', attachment.fileName)}</a>)}</div>} <span>{entry.changedByEmail || 'System'} | {formatDate(entry.createdAt)}</span> </article>)}</div></div>}
                {canOwnerCancel(selectedRequest.status) && <div className="sr-owner-actions"><Button variant="tertiary" size="sm" className="sr-cancel-btn" loading={cancellingId === selectedRequest.id} onClick={() => { void handleCancelRequest(selectedRequest); }}>Cancel request</Button></div>}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </PageLayout>
  );
}

function AdminSupportView() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState<SupportRequestStatus | ''>('');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [activeRequestId, setActiveRequestId] = useState<number | null>(null);
  const [activeStatus, setActiveStatus] = useState<SupportRequestStatus>('OPEN');
  const [activeNote, setActiveNote] = useState('');
  const [activeNoteAttachmentUrls, setActiveNoteAttachmentUrls] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getAllSupportRequests({ status: statusFilter, search });
      setRequests(rows);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to load support requests.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (requests.length === 0) {
      setActiveRequestId(null);
      return;
    }

    if (activeRequestId && !requests.some((request) => request.id === activeRequestId)) {
      setActiveRequestId(null);
    }
  }, [requests, activeRequestId]);

  const activeRequest = useMemo(
    () => requests.find((item) => item.id === activeRequestId) || null,
    [activeRequestId, requests],
  );

  const activeHistoryAttachments = useMemo(
    () => (activeRequest ? groupAttachmentsByHistory(activeRequest) : {}),
    [activeRequest],
  );

  useEffect(() => {
    if (!activeRequest) {
      setActiveStatus('OPEN');
      setActiveNote('');
      setActiveNoteAttachmentUrls([]);
      return;
    }

    setActiveStatus(activeRequest.status);
    setActiveNote('');
    setActiveNoteAttachmentUrls([]);
  }, [activeRequest]);

  const handleAdminNoteUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    if (activeNoteAttachmentUrls.length + files.length > 5) {
      showErrorToast('You can attach up to 5 files (images/videos) to a ticket note.');
      return;
    }

    setSavingId(activeRequestId ?? 0);
    try {
      const nextUrls: string[] = [];
      for (const file of files) {
        const uploadedUrl = await uploadSupportRequestImage(file);
        nextUrls.push(uploadedUrl);
      }

      setActiveNoteAttachmentUrls((current) => [...current, ...nextUrls]);
      showSuccessToast('Note attachment uploaded.');
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to upload note attachment.');
    } finally {
      setSavingId(null);
      event.target.value = '';
    }
  };

  const updateStatus = async () => {
    if (!activeRequest) {
      return;
    }

    if (!activeNote.trim()) {
      showErrorToast('A note is required for every ticket update.');
      return;
    }

    setSavingId(activeRequest.id);
    try {
      const result = await updateSupportRequestStatus(activeRequest.id, {
        status: activeStatus,
        resolutionNote: activeNote.trim(),
        attachmentUrls: activeNoteAttachmentUrls,
      });

      setRequests((current) => current.map((item) => (item.id === activeRequest.id ? result.request : item)));
      setActiveStatus(result.request.status);
      setActiveNote('');
      setActiveNoteAttachmentUrls([]);
      setActiveRequestId(null);
      showSuccessToast(`Updated ${result.request.ticketNumber || `#${result.request.id}`}. Email sent to ${result.notificationRecipient}.`);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to update support request.');
    } finally {
      setSavingId(null);
    }
  };

  const totals = useMemo(() => ({
    all: requests.length,
    open: requests.filter((item) => item.status === 'OPEN').length,
    inProgress: requests.filter((item) => item.status === 'IN_PROGRESS').length,
    waiting: requests.filter((item) => item.status === 'WAITING_ON_OWNER').length,
    resolved: requests.filter((item) => item.status === 'RESOLVED' || item.status === 'CLOSED').length,
  }), [requests]);

  if (loading) {
    return (
      <PageLayout title="Support Requests">
        <div className="sr-loading">Loading support requests...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Support Requests">
      <div className="sr-inbox sr-inbox--admin">
        <section className="sr-card">
          <div className="sr-card-header">
            <div>
              <p className="sr-eyebrow">Support queue</p>
              <h2>Tickets</h2>
            </div>
            <Button variant="tertiary" size="sm" startIcon={<FaSyncAlt />} onClick={() => { void load(); }}>
              Refresh
            </Button>
          </div>

          <div className="sr-insights">
            <div className="sr-chip"><span>Total</span><strong>{totals.all}</strong></div>
            <div className="sr-chip"><span>Open</span><strong>{totals.open}</strong></div>
            <div className="sr-chip"><span>In Progress</span><strong>{totals.inProgress}</strong></div>
            <div className="sr-chip"><span>Waiting</span><strong>{totals.waiting}</strong></div>
            <div className="sr-chip"><span>Resolved</span><strong>{totals.resolved}</strong></div>
          </div>

          <div className="sr-filters">
            <input
              className="sr-input"
              placeholder="Search by ticket, kiosk, owner, subject"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className="sr-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as SupportRequestStatus | '')}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
            <Button variant="secondary" onClick={() => { void load(); }}>Apply</Button>
          </div>

          {requests.length === 0 ? (
            <div className="sr-empty">No support requests found for this filter.</div>
          ) : (
            <div className="sr-ticket-list sr-ticket-list--dense">
              {requests.map((request) => (
                <button
                  key={request.id}
                  type="button"
                  className={`sr-ticket-row sr-ticket-row--compact ${activeRequestId === request.id ? 'sr-ticket-row--active' : ''}`}
                  aria-pressed={activeRequestId === request.id}
                  onClick={() => setActiveRequestId(request.id)}
                >
                  <div className="sr-ticket-row__line">
                    <div className="sr-ticket-row__subject-line">
                      <span className="sr-ticket"><FaHashtag /> {request.ticketNumber || request.id}</span>
                      <h3>{request.subject}</h3>
                    </div>
                    <div className="sr-ticket-row__summary sr-ticket-row__summary--admin">
                      <span className="sr-meta-icon"><FaBuilding /> {request.kioskId}</span>
                      <span className="sr-meta-icon"><FaUser /> {request.ownerEmail || 'Unknown'}</span>
                      <span className="sr-meta-icon"><FaClock /> {formatDate(request.updatedAt)}</span>
                      <span className="sr-meta-icon"><FaPaperclip /> {request.attachments.length}</span>
                      <span className="sr-priority-inline">
                        <PriorityBadge value={request.priority} />
                      </span>
                    </div>
                    <div className={`sr-status sr-status--${request.status.toLowerCase()}`}>
                      {STATUS_LABELS[request.status]}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {activeRequest && createPortal(
        <div className="sr-popup-overlay" role="dialog" aria-modal="true" aria-labelledby="sr-admin-ticket-title" onClick={(event) => { if (event.target === event.currentTarget) setActiveRequestId(null); }}>
          <div className="sr-popup-card sr-popup-card--wide">
            <div className="sr-modal-header">
              <div>
                <p className="sr-ticket">{activeRequest.ticketNumber || `#${activeRequest.id}`}</p>
                <h3 id="sr-admin-ticket-title">{activeRequest.subject}</h3>
              </div>
              <Button variant="tertiary" size="sm" onClick={() => setActiveRequestId(null)}>Close</Button>
            </div>

            <div className="sr-popup-card__body">
              <div className="sr-modal-meta">
                <span className={`sr-status sr-status--${activeRequest.status.toLowerCase()}`}>{STATUS_LABELS[activeRequest.status]}</span>
                <span className="sr-priority-inline"><PriorityBadge value={activeRequest.priority} /></span>
                <span className="sr-meta-icon"><FaBuilding /> {activeRequest.kioskId}</span>
                <span className="sr-meta-icon"><FaUser /> {activeRequest.ownerEmail || 'Unknown'}</span>
                <span className="sr-meta-icon"><FaClock /> Created {formatDate(activeRequest.createdAt)}</span>
                <span className="sr-meta-icon"><FaClock /> Updated {formatDate(activeRequest.updatedAt)}</span>
                <span className="sr-meta-icon"><FaPaperclip /> {activeRequest.attachments.length}</span>
              </div>

              <div className="sr-update-panel">
                <div className="sr-update-panel__header">
                  <h4>Update ticket</h4>
                </div>

                <div className="sr-update-form sr-update-form--stacked">
                  <label className="sr-field sr-field--grow">
                    <span>Update note (required)</span>
                    <textarea
                      className="sr-textarea sr-textarea--compact"
                      rows={3}
                      value={activeNote}
                      onChange={(event) => setActiveNote(event.target.value)}
                      placeholder="Add a note explaining this ticket update"
                    />
                  </label>

                  <div className="sr-attachments-row">
                    <label className="sr-upload-btn">
                      <FaPaperclip /> Add note files
                      <input type="file" accept="image/*,video/*" multiple onChange={handleAdminNoteUpload} disabled={savingId !== null} />
                    </label>
                    <span className="sr-upload-meta">Include screenshots/videos or evidence with this update.</span>
                  </div>

                  {activeNoteAttachmentUrls.length > 0 && (
                    <div className="sr-attachment-preview-grid">
                      {activeNoteAttachmentUrls.map((url, index) => (
                        <div key={url} className="sr-attachment-preview-item">
                          {renderAttachmentMedia(url, `Admin note attachment ${index + 1}`)}
                          <button type="button" className="sr-remove-link" onClick={() => setActiveNoteAttachmentUrls((current) => current.filter((item) => item !== url))}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="sr-update-inline-controls">
                    <label className="sr-field sr-field--compact">
                      <span>Status</span>
                      <select
                        className="sr-select"
                        value={activeStatus}
                        onChange={(event) => setActiveStatus(event.target.value as SupportRequestStatus)}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {STATUS_LABELS[status]}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="sr-update-form__actions">
                      <Button
                        variant="primary"
                        onClick={() => {
                          void updateStatus();
                        }}
                        loading={savingId === activeRequest.id}
                      >
                        Save update
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {activeRequest.description && <p className="sr-description">{activeRequest.description}</p>}

              {activeRequest.statusHistory.length > 0 && (
                <div className="sr-history-block">
                  <h4>Ticket history</h4>
                  <div className="sr-history-list">
                    {activeRequest.statusHistory.map((entry) => (
                      <article key={entry.id} className="sr-history-item">
                        <p>
                          <strong>{entry.fromStatus || 'NEW'}</strong>
                          {' -> '}
                          <strong>{entry.toStatus}</strong>
                        </p>
                        <p>{entry.note || 'No note provided'}</p>
                        {(activeHistoryAttachments[entry.id] || []).length > 0 && (
                          <div className="sr-history-attachments-grid">
                            {(activeHistoryAttachments[entry.id] || []).map((attachment) => (
                              <a key={attachment.id} href={attachment.imageUrl} target="_blank" rel="noreferrer" className="sr-attachment-tile">
                                {renderAttachmentMedia(attachment.imageUrl, attachment.fileName || 'Attachment', attachment.fileName)}
                              </a>
                            ))}
                          </div>
                        )}
                        <span>
                          {entry.changedByEmail || 'System'} | {formatDate(entry.createdAt)}
                        </span>
                      </article>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </PageLayout>
  );
}

export default function SupportRequestsPage() {
  const isKioskOwner = useIsKioskOwner();
  return isKioskOwner ? <OwnerSupportView /> : <AdminSupportView />;
}
